import { useCallback, useMemo, useState } from "react";
import { useReduxState } from "hooks/useReduxState";
import { useDispatch } from "react-redux";
import { Corp, MstL, MstM, MstS } from "models/CorpScreening";
import {
  CompanyResponse,
  RecentCompanyItem,
  SourceItem
} from "models/CompanyModal";
import { setCompanySelectSourceItems } from "modules/tool/screening/corp/actions";
import { useIpAuthFlg } from "hooks/useIpAuthFlg";
import { actionCreators as commonActionCreators } from "modules/common/BinderList";

// 企業名から企業項目
interface SearchListItem {
  companyCode: string;
  companyName: string;
  stkCode: string;
  source: string;
  companyKbn?: string;
}

export const useSearchList = ({
  fetchCompanyBySuggest,
  ignoreInit = false,
  defaultKeyword,
  disabledCodes,
  searchType
}: {
  fetchCompanyBySuggest: ({
    companyName,
    suggestFlag,
    suggestMeta
  }: {
    companyName: string;
    suggestFlag?: boolean;
    suggestMeta?: string;
  }) => Promise<SearchListItem[]>;
  ignoreInit?: boolean;
  defaultKeyword?: string;
  disabledCodes?: string[];
  searchType?: string;
}) => {
  const [searchResult, setSearchResult] = useState<SearchListItem[] | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const type = useReduxState(
    state => state.commonSourceSelector.authType.toolScreeningCorpItem
  );

  const searchCorp = async ({
    companyName,
    suggestFlag,
    suggestMeta = ""
  }: {
    companyName: string;
    suggestFlag?: boolean;
    suggestMeta?: string;
  }) => {
    setIsLoading(true);

    const companyList = await fetchCompanyBySuggest({
      companyName,
      suggestFlag,
      suggestMeta
    });
    setSearchResult(companyList);
    setIsLoading(false);
  };

  return {
    searchResult,
    searchCorp,
    type: searchType || type,
    isLoading,
    ignoreInit,
    defaultKeyword,
    disabledCodes
  };
};

export const useSelectMenu = ({
  uncontrolled,
  disableOverseas,
  hiddenTypeSelection,
  hiddenMainFlag = false,
  disabledCodes,
  fetchNeedsMstL,
  fetchNeedsMstM,
  fetchNeedsMstS,
  fetchCorpList
}: {
  uncontrolled: boolean;
  disableOverseas: boolean;
  hiddenTypeSelection: boolean;
  hiddenMainFlag?: boolean;
  disabledCodes?: string[];
  fetchNeedsMstL: () => Promise<MstL[]>;
  fetchNeedsMstM: ({ nkgncodel }: { nkgncodel: string }) => Promise<MstM[]>;
  fetchNeedsMstS: ({
    nkgncodel,
    nkgncodem
  }: {
    nkgncodel: string;
    nkgncodem: string;
  }) => Promise<MstS[]>;
  fetchCorpList: ({
    nkgncodes,
    mainFlgComp,
    allChecked,
    concatenatedCorpFilterValue,
    isFilterUpdate,
    corpIndustyCode
  }: {
    nkgncodes: string;
    mainFlgComp: string;
    allChecked: boolean;
    concatenatedCorpFilterValue: string;
    isFilterUpdate?: string;
    corpIndustyCode?: string;
  }) => Promise<Corp[]>;
}) => {
  const [needsMstL, setNeedsMstL] = useState<MstL[]>([]);
  const [needsMstM, setNeedsMstM] = useState<MstM[]>([]);
  const [needsMstS, setNeedsMstS] = useState<MstS[]>([]);
  const [corpList, setCorpList] = useState<Corp[]>([]);
  const [loadingMstM, setLoadingMstM] = useState<boolean>(false);
  const [loadingMstS, setLoadingMstS] = useState<boolean>(false);
  const [loadingCorp, setLoadingCorp] = useState<boolean>(false);
  const [selectedmstCodeL, setSelectedmstCodeL] = useState<string>("");
  const [selectedmstCodeM, setSelectedmstCodeM] = useState<string>("");
  const [selectedmstCodeS, setSelectedmstCodeS] = useState<string>("");

  const sourceItems = useReduxState<SourceItem[]>(
    state => state.toolScreeningCorpItemModern.item.companySelectSourceItems
  );
  const personalSettings = useReduxState(
    state => state.commonSourceSelector.personalSettings
  );

  const dispatch = useDispatch();

  const getNeedsMstL = async () => {
    if (needsMstL.length) return;

    setNeedsMstM([]);
    setNeedsMstS([]);
    setCorpList([]);

    const response = await fetchNeedsMstL();
    setNeedsMstL(response);
  };

  const getNeedsMstM = async ({ nkgncodel }: { nkgncodel: string }) => {
    setNeedsMstS([]);
    setCorpList([]);
    setLoadingMstM(true);
    setSelectedmstCodeL(nkgncodel);

    const response = await fetchNeedsMstM({ nkgncodel });
    setNeedsMstM(response);
    setLoadingMstM(false);
  };

  const getNeedsMstS = async ({
    nkgncodel,
    nkgncodem
  }: {
    nkgncodel: string;
    nkgncodem: string;
  }) => {
    setCorpList([]);
    setSelectedmstCodeL(nkgncodel);
    setSelectedmstCodeM(nkgncodem);
    setLoadingMstS(true);

    const response = await fetchNeedsMstS({ nkgncodel, nkgncodem });

    setNeedsMstS(response);
    setLoadingMstS(false);
  };

  const onClickNeedsMstS = async (params: {
    mainFlgComp: string;
    nkgncodes: string;
    allChecked: boolean;
    concatenatedCorpFilterValue: string;
    isFilterUpdate?: string;
    corpIndustyCode?: string;
  }) => {
    setSelectedmstCodeS(params.nkgncodes);
    setLoadingCorp(true);
    const response = await fetchCorpList({
      nkgncodes: params.nkgncodes,
      mainFlgComp: params.mainFlgComp,
      allChecked: params.allChecked,
      concatenatedCorpFilterValue: params.concatenatedCorpFilterValue,
      isFilterUpdate: params.isFilterUpdate,
      corpIndustyCode: params.corpIndustyCode
    });

    setCorpList(response);
    setLoadingCorp(false);
  };

  const initSourceItems = () => {
    dispatch(setCompanySelectSourceItems([...personalSettings]));
  };

  const clear = () => {
    setNeedsMstM([]);
    setNeedsMstS([]);
    setCorpList([]);
  };

  return {
    corpList,
    getNeedsMstL,
    getNeedsMstM,
    getNeedsMstS,
    onClickNeedsMstS,
    clear,
    initSourceItems,
    needsMstL,
    needsMstM,
    needsMstS,
    sourceItems,
    loadingMstM,
    loadingMstS,
    loadingCorp,
    disableOverseas,
    hiddenTypeSelection,
    hiddenMainFlag,
    ...(!uncontrolled && {
      selectedmstCodeL,
      selectedmstCodeM,
      selectedmstCodeS
    }),
    disabledCodes
  };
};

export const useRecentList = ({
  hiddenRecent = false,
  fetchRecentList,
  disabledCodes
}: {
  hiddenRecent?: boolean;
  fetchRecentList: () => Promise<CompanyResponse[]>;
  disabledCodes?: string[];
}) => {
  const [recentList, setRecentList] = useState<RecentCompanyItem[]>([]);

  const getRecent = async () => {
    const response = await fetchRecentList();
    setRecentList(
      response.map(item => ({
        ...item,
        stkCode: item.kabushikiCode
      }))
    );
  };

  return {
    getRecent,
    recentList,
    hiddenRecent,
    disabledCodes
  };
};

export const useMyCollection = ({
  corpFilter,
  disabledCodes,
  excludeBinderId
}: {
  corpFilter: string;
  disabledCodes?: string[];
  excludeBinderId?: string;
}) => {
  const collections = useReduxState(state => state.commonBinderList.list);
  const corpList = useReduxState(state => state.commonBinderList.items);
  const isLoading = useReduxState(
    state => state.commonBinderList.isLoading.items
  );
  const isBinderLoading = useReduxState(
    state => state.commonBinderList.isLoading.list
  );

  const dispatch = useDispatch();
  const ipAuthFlg = useIpAuthFlg();
  const [keyword, setKeyword] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string>("");

  const getBinders = useCallback(
    (keyword?: string) => {
      if (ipAuthFlg) return; // IP認証の場合は処理しない
      keyword ? setKeyword(keyword) : setKeyword("");
      dispatch(
        commonActionCreators.getBinderList({
          keyword,
          binderFilter: "0",
          categoryCode: "VS02",
          excludeBinderId
        })
      );
      setSelectedId("");
    },
    [dispatch, ipAuthFlg, excludeBinderId]
  );

  const onSelectCollection = (id: string) => {
    setSelectedId(id);
    dispatch(
      commonActionCreators.getBinderItem({
        binderId: id,
        corpFilter: corpFilter
      })
    );
  };

  const corporations = useMemo(() => {
    return corpList.map((item: { corpName: string; corpCode: string }) => ({
      ...item,
      companyName: item.corpName,
      companyCode: item.corpCode
    }));
  }, [corpList]);

  return {
    collections,
    corporations,
    isLoading,
    getBinders,
    keyword,
    setKeyword,
    onSelectCollection,
    isBinderLoading,
    selectedId,
    disabledCodes
  };
};

export const useVsCollection = ({
  corpFilter,
  disabledCodes,
  excludeBinderId
}: {
  corpFilter: string;
  disabledCodes?: string[];
  excludeBinderId?: string;
}) => {
  const collections = useReduxState(state => state.commonBinderList.vsList);
  const corpList = useReduxState(state => state.commonBinderList.vsItems);
  const isLoading = useReduxState(
    state => state.commonBinderList.isLoading.vsItems
  );
  const isBinderLoading = useReduxState(
    state => state.commonBinderList.isLoading.vsList
  );

  const dispatch = useDispatch();
  const ipAuthFlg = useIpAuthFlg();
  const [keyword, setKeyword] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string>("");

  const getBinders = useCallback(
    (keyword?: string) => {
      if (ipAuthFlg) return; // IP認証の場合は処理しない
      keyword ? setKeyword(keyword) : setKeyword("");
      dispatch(
        commonActionCreators.getVsBinderList({
          keyword,
          binderFilter: "1",
          categoryCode: "VS02",
          excludeBinderId
        })
      );
      setSelectedId("");
    },
    [dispatch, ipAuthFlg, excludeBinderId]
  );

  const onSelectCollection = (id: string) => {
    setSelectedId(id);
    dispatch(
      commonActionCreators.getVsBinderItem({
        binderId: id,
        corpFilter: corpFilter
      })
    );
  };

  const corporations = useMemo(() => {
    return corpList.map((item: { corpName: string; corpCode: string }) => ({
      ...item,
      companyName: item.corpName,
      companyCode: item.corpCode
    }));
  }, [corpList]);

  return {
    collections,
    corporations,
    isLoading,
    getBinders,
    keyword,
    setKeyword,
    onSelectCollection,
    isBinderLoading,
    selectedId,
    disabledCodes
  };
};
